<template>
  <div
    ref="sidebarRef"
    class="bg-sidebar hit-menu-sidebar"
    :class="{
      'w-0': hide,
      'w-14': !hide && localCollapsed,
      'w-64': !hide && !localCollapsed,
      collapsed: localCollapsed || hide,
      expanded: !localCollapsed && !hide,
    }"
  >
    <div class="px-2 pt-2 content overflow-y-auto overflow-x-hidden">
      <hit-menu-item
        v-for="menu in menuItems"
        :key="menu.id"
        :to="menu.getRouterTo()"
        :replace="menu.replace"
        :icon="menu.icon"
        :label="$t(menu.titleKey)"
        :collapse="localCollapsed"
        :hide="false"
        :selected="isSelected(menu)"
        @click="onMenuItemClick(menu)"
      />
    </div>
    <div
      class="collapse-icon"
      :class="{
        expanded: !localCollapsed && !hide,
        collapsed: localCollapsed || hide,
      }"
    >
      <hit-button
        v-if="responsiveBreakpointMd"
        :prefix-icon="collapseIcon"
        color="sidebar"
        hide-border
        :remove-outline="true"
        icon-size="lg"
        :tooltip="collapseTooltip"
        @click="toggleSidebarCollapse"
      />
    </div>
  </div>
</template>

<script>
import HitMenuItem from '@/components/hit/menu/HitMenuItem.vue';
import {Menu} from '@hit/base';
import {HitBreakpointsMixin, HitButton} from '@hit/components';

export default {
  name: 'AdminSidebar',
  components: {HitMenuItem, HitButton},
  mixins: [HitBreakpointsMixin],
  props: {
    collapsed: {type: Boolean, default: false},
    hide: {type: Boolean, default: false},
  },
  computed: {
    menuItems() {
      // Static menu
      return [
        new Menu('', 'home', 'hit-admin.common.home', 'home', [], 'admin-base'),
        new Menu(
          'tracker',
          'trackerList',
          'hit-admin.common.tracker',
          'tracker',
          [],
          'admin-base'
        ),
        new Menu(
          'reports',
          'reports',
          'hit-admin.common.reports',
          'description',
          [],
          'admin-base'
        ),
        new Menu(
          'scripts',
          'scripts',
          'hit-admin.common.scripts',
          'scripts',
          [],
          'admin-base'
        ),
        new Menu(
          'subscriptions',
          'subscriptions',
          'hit-admin.common.subscriptions',
          'credit-card',
          [],
          'admin-base'
        ),
        new Menu(
          'notifications',
          'notifications',
          'hit-admin.common.notifications',
          'notification',
          [],
          'admin-base'
        ),
        new Menu(
          'settings',
          'settings',
          'hit-admin.common.settings',
          'advanced_settings',
          [],
          'admin-base'
        ),
      ];
    },
    collapseTooltip() {
      return this.computedCollapsed
        ? this.$t('hit-admin.common.click-to-expand')
        : this.$t('hit-admin.common.click-to-collapse');
    },
    collapseIcon() {
      return this.computedCollapsed ? 'expand-right' : 'expand-left';
    },
    computedCollapsed() {
      return this.localCollapsed;
    },
  },
  methods: {
    isSelected(menu) {
      const routeMenu = this.$route.fullPath.split('/')[1].split('-')[0];
      return (
        menu.id === routeMenu ||
        (menu.id === '' && (this.$route.path === '/' || !routeMenu))
      );
    },
    onMenuItemClick(item) {
      this.$emit('menuItemClick', item);
    },
    toggleSidebarCollapse() {
      this.localCollapsed = !this.localCollapsed;
    },
    openFeedback() {
      this.feedbackModal = true;
    },
    handleClickOutside(event) {
      // Only close sidebar on mobile (below md breakpoint) when it's visible
      if (!this.responsiveBreakpointMd && !this.hide && this.$refs.sidebarRef) {
        const target = event.target;

        const isClickOnModal =
          target.closest('.modal-container') ||
          target.closest('.modal-overlay');
        if (isClickOnModal) return;

        const isClickInsideSidebar = this.$refs.sidebarRef.contains(target);
        if (!isClickInsideSidebar) {
          this.$emit('close');
        }
      }
    },
  },
  emits: ['menuItemClick', 'close'],
  data() {
    return {
      feedbackModal: false,
      localCollapsed: this.collapsed,
    };
  },
  watch: {
    collapsed(newVal) {
      this.localCollapsed = newVal;
    },
    responsiveBreakpointMd: {
      handler(newValue) {
        if (!newValue) {
          // Ensure the menu is expanded when entering mobile mode
          this.localCollapsed = false;
        }
      },
      immediate: true,
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('touchstart', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('touchstart', this.handleClickOutside);
  },
};
</script>

<style lang="scss">
.hit-menu-sidebar {
  height: 100%;
  display: grid;
  grid-template-rows: minmax(0, 1fr) max-content;
  grid-template-areas:
    'menu-content'
    'menu-collapse';
  position: relative;
  z-index: 30;
  .hit-button {
    @apply p-0;
  }
  &.collapsed {
    transition: width 0.3s ease-in-out, padding 0.3s ease-in-out;
  }
  &.expanded {
    transition: width 0.3s ease-in-out, padding 0.3s ease-in-out;
  }
  .content {
    grid-area: menu-content;
    @apply grid gap-2 items-center;
    grid-template-rows: repeat(auto-fit, minmax(2.25rem, max-content));
  }
  .collapse-icon {
    grid-area: menu-collapse;
    @apply grid items-center mt-2;
    align-self: end;
    &.collapsed {
      grid-template-columns: 1fr;
    }
    .hit-button {
      width: 100%;
      justify-self: stretch;
    }
  }
}
</style>
